import { useState, useEffect, useCallback } from 'react';
import { getPhotoUrl } from '../services/photoService';
import type { ProjectPhotoView } from '../types';

interface IconWithTooltipProps {
  icon: React.ReactNode;
  tooltip: string;
  bgColor: string;
}

function IconWithTooltip({ icon, tooltip, bgColor }: IconWithTooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleTap = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setShowTooltip((prev) => !prev);
    // Auto-hide after 2 seconds on mobile
    setTimeout(() => setShowTooltip(false), 2000);
  }, []);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleTap}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`w-6 h-6 ${bgColor} rounded-full flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity`}
        aria-label={tooltip}
      >
        {icon}
      </button>
      {showTooltip && (
        <div className="absolute right-0 top-full mt-1 z-10 px-2 py-1 text-xs text-white bg-secondary-800 rounded shadow-lg whitespace-nowrap">
          {tooltip}
          <div className="absolute -top-1 right-2 w-2 h-2 bg-secondary-800 rotate-45" />
        </div>
      )}
    </div>
  );
}

interface SelectablePhotoGalleryProps {
  photos: ProjectPhotoView[];
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  onPhotoClick?: (photo: ProjectPhotoView) => void;
  isSelectionMode: boolean;
  emptyMessage?: string;
}

export function SelectablePhotoGallery({
  photos,
  selectedIds,
  onSelectionChange,
  onPhotoClick,
  isSelectionMode,
  emptyMessage = 'No photos yet',
}: SelectablePhotoGalleryProps) {
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loadingUrls, setLoadingUrls] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadUrls = async () => {
      for (const photo of photos) {
        if (!signedUrls[photo.id] && !loadingUrls.has(photo.id)) {
          setLoadingUrls((prev) => new Set(prev).add(photo.id));
          try {
            const url = await getPhotoUrl(photo.filePath);
            setSignedUrls((prev) => ({ ...prev, [photo.id]: url }));
          } catch (error) {
            console.error(`Error loading URL for photo ${photo.id}:`, error);
          } finally {
            setLoadingUrls((prev) => {
              const next = new Set(prev);
              next.delete(photo.id);
              return next;
            });
          }
        }
      }
    };

    loadUrls();
  }, [photos, signedUrls, loadingUrls]);

  const handlePhotoClick = (photo: ProjectPhotoView) => {
    if (isSelectionMode) {
      const newSelection = new Set(selectedIds);
      if (newSelection.has(photo.id)) {
        newSelection.delete(photo.id);
      } else {
        newSelection.add(photo.id);
      }
      onSelectionChange(newSelection);
    } else {
      onPhotoClick?.(photo);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.size === photos.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(photos.map((p) => p.id)));
    }
  };

  if (photos.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary-50 rounded-lg">
        <svg
          className="w-12 h-12 mx-auto text-secondary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="mt-2 text-secondary-600">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Select All (only in selection mode) */}
      {isSelectionMode && photos.length > 0 && (
        <div className="flex items-center gap-2">
          <button
            onClick={handleSelectAll}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {selectedIds.size === photos.length ? 'Deselect All' : 'Select All'}
          </button>
          <span className="text-sm text-secondary-500">
            ({selectedIds.size} of {photos.length} selected)
          </span>
        </div>
      )}

      {/* Photo Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {photos.map((photo) => {
          const isSelected = selectedIds.has(photo.id);

          return (
            <div
              key={photo.id}
              className={`
                group relative aspect-square bg-secondary-100 rounded-lg overflow-hidden cursor-pointer
                transition-all
                ${isSelectionMode
                  ? isSelected
                    ? 'ring-2 ring-primary-500 ring-offset-2'
                    : 'hover:ring-2 hover:ring-secondary-300'
                  : 'hover:ring-2 hover:ring-primary-500'
                }
              `}
              onClick={() => handlePhotoClick(photo)}
            >
              {signedUrls[photo.id] ? (
                <img
                  src={signedUrls[photo.id]}
                  alt={photo.fileName}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-secondary-400 animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}

              {/* Selection Checkbox */}
              {isSelectionMode && (
                <div
                  className={`
                    absolute top-2 left-2 w-6 h-6 rounded-full border-2 
                    flex items-center justify-center transition-colors
                    ${isSelected
                      ? 'bg-primary-500 border-primary-500'
                      : 'bg-white/80 border-secondary-300'
                    }
                  `}
                >
                  {isSelected && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              )}

              {/* Overlay on hover (non-selection mode) */}
              {!isSelectionMode && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                  <div className="w-full p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs truncate">{photo.fileName}</p>
                    {photo.takenAt && (
                      <p className="text-white/80 text-xs">
                        {new Date(photo.takenAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Indicator badges */}
              {!isSelectionMode && (
                <div className="absolute top-2 right-2 flex gap-1">
                  {/* Notes indicator */}
                  {photo.description && (
                    <IconWithTooltip
                      tooltip="Photo has notes"
                      bgColor="bg-primary-500"
                      icon={
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8 12h8v2H8zm0 4h8v2H8z" />
                        </svg>
                      }
                    />
                  )}
                  {/* GPS indicator */}
                  {photo.hasCoordinates && (
                    <IconWithTooltip
                      tooltip="Photo has GPS location"
                      bgColor="bg-green-500"
                      icon={
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                        </svg>
                      }
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}